import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { staffAPI } from '../services/api';
import LecturerSidebar from '../components/LecturerSidebar';

const LecturerSettings = () => {
  const { currentUser } = useAuth();
  const [staffInfo, setStaffInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    employeeId: '',
    address: ''
  });

  // Fetch lecturer profile from database
  useEffect(() => {
    const fetchLecturerProfile = async () => {
      if (currentUser && currentUser.role === 'lecturer') {
        try {
          setLoading(true);
          console.log('=== FETCHING LECTURER PROFILE ===');
          console.log('Current user:', currentUser);
          console.log('User role:', currentUser.role);
          console.log('User email:', currentUser.email);
          
          const response = await staffAPI.getProfile();
          console.log('Profile fetch response:', response);
          
          const profileData = response.data;
          setStaffInfo(profileData);
          
          // Parse name into first and last name
          const nameParts = profileData.name ? profileData.name.split(' ') : ['', ''];
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          setFormData({
            firstName: firstName,
            lastName: lastName,
            email: profileData.email || '',
            phoneNumber: profileData.contact_no || '',
            employeeId: profileData.employee_id || '',
            address: profileData.address || ''
          });
        } catch (error) {
          console.error('Error fetching lecturer profile:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error data:', error.response?.data);
          setMessage('Failed to load profile information');
        } finally {
          setLoading(false);
        }
      } else {
        console.log('User not ready or not a lecturer:', {
          currentUser: !!currentUser,
          role: currentUser?.role
        });
      }
    };

    fetchLecturerProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      setMessage('');
      
      console.log('=== SAVING LECTURER PROFILE ===');
      console.log('Current user:', currentUser);
      console.log('User role:', currentUser?.role);
      console.log('User email:', currentUser?.email);
      
      // Only save address and phoneNumber
      const updateData = {
        address: formData.address,
        phoneNumber: formData.phoneNumber
      };
      
      console.log('Saving changes:', updateData);
      
      const response = await staffAPI.updateProfile(updateData);
      console.log('Update response:', response);
      
      if (response.data) {
        setMessage('Settings saved successfully!');
        // Update local state with the returned data
        if (response.data.data) {
          setStaffInfo(response.data.data);
          
          // Update form data with new values
          setFormData(prev => ({
            ...prev,
            phoneNumber: response.data.data.contact_no || prev.phoneNumber,
            address: response.data.data.address || prev.address
          }));
        }
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      let errorMessage = 'Failed to save settings. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to update this profile.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setMessage(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: '#F5F6FA', overflow: 'hidden'}}>
      <LecturerSidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh', background: '#F5F6FA', position: 'relative'}}>
        {/* Page Title */}
        <div style={{padding: '32px 40px 20px 40px'}}>
          <h1 style={{fontSize: 36, fontFamily: 'Outfit, sans-serif', fontWeight: '700', color: '#131A1A', margin: 0}}>
            Settings
          </h1>
          <p style={{fontSize: 16, color: '#798AA3', margin: '8px 0 0 0', fontFamily: 'Outfit, sans-serif'}}>
            Manage your personal information and preferences
          </p>
        </div>

        {/* Settings Content */}
        <div style={{margin: '0 40px 40px 40px'}}>
          <div style={{background: '#F04915', borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: '24px 32px'}}>
            <h2 style={{color: 'white', fontSize: 24, fontFamily: 'Outfit, sans-serif', fontWeight: '700', margin: 0}}>
              Personal Settings
            </h2>
            <p style={{color: 'white', fontSize: 14, fontFamily: 'Outfit, sans-serif', margin: '8px 0 0 0', opacity: 0.9}}>
              Update your profile information and contact details
            </p>
          </div>

          <div style={{background: 'white', padding: '40px', borderBottomLeftRadius: 16, borderBottomRightRadius: 16, boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
            {/* Personal Information */}
            <div style={{marginBottom: '40px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', marginBottom: '20px', color: '#131A1A'}}>
                Personal Information
              </h3>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: '#131A1A', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    readOnly
                    style={{
                      width: '100%', 
                      height: 48, 
                      padding: '0 16px', 
                      borderRadius: 12, 
                      border: '1px solid #E5E7EB',
                      fontSize: 14,
                      fontFamily: 'Outfit, sans-serif',
                      color: '#6B7280',
                      outline: 'none',
                      background: '#F9FAFB',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: '#131A1A', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    readOnly
                    style={{
                      width: '100%', 
                      height: 48, 
                      padding: '0 16px', 
                      borderRadius: 12, 
                      border: '1px solid #E5E7EB',
                      fontSize: 14,
                      fontFamily: 'Outfit, sans-serif',
                      color: '#6B7280',
                      outline: 'none',
                      background: '#F9FAFB',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: '#131A1A', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                    Employee ID
                  </label>
                  <input
                    type="text"
                    name="employeeId"
                    value={formData.employeeId}
                    readOnly
                    style={{
                      width: '100%', 
                      height: 48, 
                      padding: '0 16px', 
                      borderRadius: 12, 
                      border: '1px solid #E5E7EB',
                      fontSize: 14,
                      fontFamily: 'Outfit, sans-serif',
                      color: '#6B7280',
                      outline: 'none',
                      background: '#F9FAFB',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: '#131A1A', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your address"
                    style={{
                      width: '100%', 
                      height: 48, 
                      padding: '0 16px', 
                      borderRadius: 12, 
                      border: '1px solid #E5E7EB',
                      fontSize: 14,
                      fontFamily: 'Outfit, sans-serif',
                      color: '#131A1A',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#F04915'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div style={{marginBottom: '40px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', marginBottom: '20px', color: '#131A1A'}}>
                Contact Information
              </h3>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: '#131A1A', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    readOnly
                    style={{
                      width: '100%', 
                      height: 48, 
                      padding: '0 16px', 
                      borderRadius: 12, 
                      border: '1px solid #E5E7EB',
                      fontSize: 14,
                      fontFamily: 'Outfit, sans-serif',
                      color: '#6B7280',
                      outline: 'none',
                      background: '#F9FAFB',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '8px', color: '#131A1A', fontSize: 14, fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="+94 77 123 4567"
                    style={{
                      width: '100%', 
                      height: 48, 
                      padding: '0 16px', 
                      borderRadius: 12, 
                      border: '1px solid #E5E7EB',
                      fontSize: 14,
                      fontFamily: 'Outfit, sans-serif',
                      color: '#131A1A',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#F04915'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '16px', alignItems: 'center'}}>
              {message && (
                <span style={{
                  color: message.includes('success') ? '#10B981' : '#EF4444',
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: '500'
                }}>
                  {message}
                </span>
              )}
              <button style={{
                padding: '12px 24px',
                background: 'transparent',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: 12,
                fontSize: 14,
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#F9FAFB';
                e.target.style.borderColor = '#D1D5DB';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = '#E5E7EB';
              }}>
                Cancel
              </button>
              <button 
                onClick={handleSaveChanges}
                disabled={saving}
                style={{
                  padding: '12px 32px',
                  background: saving ? '#9CA3AF' : '#F04915',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontSize: 14,
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: '500',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0px 4px 12px rgba(240, 73, 21, 0.3)'
                }}
                onMouseOver={(e) => {
                  if (!saving) {
                    e.target.style.background = '#E04015';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0px 6px 20px rgba(240, 73, 21, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!saving) {
                    e.target.style.background = '#F04915';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0px 4px 12px rgba(240, 73, 21, 0.3)';
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            {/* Help Section */}
            <div style={{
              background: 'linear-gradient(135deg, #ff6600ff 0%, #EC9F05 100%)',
              borderRadius: 12,
              padding: 24,
              marginTop: 20,
              color: 'white',
              textAlign: 'center'
            }}>
              <h4 style={{
                fontSize: 18,
                fontWeight: 600,
                margin: '0 0 12px 0',
                fontFamily: 'Outfit, sans-serif'
              }}>
                💡 Need Help?
              </h4>
              <p style={{
                fontSize: 14,
                margin: 0,
                fontFamily: 'Outfit, sans-serif',
                opacity: 0.9
              }}>
                If you need to update restricted fields like your name or email, please contact the academic office.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LecturerSettings;



